import { create } from "zustand";
import type { ConnectionState, GatewayClient } from "@/lib/gateway-client";
import type { GatewayRpc } from "@/lib/gateway-rpc";

/**
 * Feature C (runtime-reinstate-and-fleet-state-plan.md) — the unified runtime
 * lifecycle state surfaced by `/api/agent/connect` (mirror of lexserver
 * `runtime-state.ts`). Drives the connection icon's colour/glyph + the
 * disconnect message so the lawyer sees *why* the workspace is unreachable
 * (starting / being repaired / unavailable) instead of a generic "Connecting…".
 */
export type RuntimeState =
  | "running"
  | "healthy"
  | "unhealthy"
  | "restarting"
  | "exited"
  | "missing"
  | "provisioning"
  | "stopped"
  | "orphan";

interface GatewayState {
  client: GatewayClient | null;
  rpc: GatewayRpc | null;
  connectionState: ConnectionState;
  /** Feature C — last runtime state reported by /api/agent/connect. */
  runtimeState: RuntimeState | null;
  /** Feature C — a Repair (reinstate) is recreating this user's runtime. */
  repairInProgress: boolean;
  /**
   * Calm auto-recovery mode — set when the gateway socket closed with
   * code 1008 (policy/auth: the planned per-user-runtime-recreate
   * class) and the client is silently re-establishing. The UI renders
   * a calm "workspace updating" surface and suppresses alarming
   * disconnect/error surfaces while this is true. See
   * gateway-token-convergence-plan.md §Layer 2.
   */
  maintenance: boolean;
  error: string | null;
  serverVersion: string | null;

  setClient: (client: GatewayClient, rpc: GatewayRpc) => void;
  setConnectionState: (state: ConnectionState) => void;
  setMaintenance: (maintenance: boolean) => void;
  setError: (error: string | null) => void;
  setServerVersion: (version: string) => void;
  setRuntimeStatus: (runtimeState: RuntimeState | null, repairInProgress: boolean) => void;
  clearClient: () => void;
}

export const useGatewayStore = create<GatewayState>((set) => ({
  client: null,
  rpc: null,
  connectionState: "disconnected",
  runtimeState: null,
  repairInProgress: false,
  maintenance: false,
  error: null,
  serverVersion: null,

  setClient: (client, rpc) => set({ client, rpc }),
  setRuntimeStatus: (runtimeState, repairInProgress) =>
    set({ runtimeState, repairInProgress }),
  setConnectionState: (connectionState) => {
    set({ connectionState });
    // DEV-only: expose the live RPC handle for Playwright specs that
    // need to drive gateway calls directly (seeding AIEditor docs via
    // `document.create`, etc.). Only assign once the gateway is fully
    // connected — callers that invoke RPCs during `authenticating`
    // see `Gateway not connected` errors. Production builds strip
    // this branch via `import.meta.env.DEV`.
    if (import.meta.env.DEV && typeof window !== "undefined") {
      if (connectionState === "connected") {
        const { rpc } = useGatewayStore.getState();
        if (rpc) (window as unknown as { __rpc?: GatewayRpc }).__rpc = rpc;
      } else {
        delete (window as unknown as { __rpc?: GatewayRpc }).__rpc;
      }
    }
  },
  setMaintenance: (maintenance) => set({ maintenance }),
  setError: (error) => set({ error }),
  setServerVersion: (serverVersion) => set({ serverVersion }),
  clearClient: () => {
    set({ client: null, rpc: null, connectionState: "disconnected", maintenance: false, error: null });
    if (import.meta.env.DEV && typeof window !== "undefined") {
      delete (window as unknown as { __rpc?: GatewayRpc }).__rpc;
    }
  },
}));
